import translate from '@translate';
import React, { FC } from 'react';
import Grid, { GridItem } from 'src/components/Grid';
import Select from 'src/components/Select';
import TextField from 'src/components/TextField';
import { ReceivingType } from 'src/types/receiving';
import { ConstantsCodeType } from 'src/types/constantsCodes';
import Typography from 'src/components/Typography';
import DateTimePicker from 'src/components/DateTimePicker';
import colorOptionFce, { normalizeBoolNumberValue } from 'src/utils/colorOptionFce';
import { FormErrorsType, OnChangeType } from '../../types/global';
import { EditKeysType } from './constants';
import Tooltip from '../../components/Tooltip';
import { InfoIcon } from '../../components/SkladonIcon/Icons';
import { styled } from '@mui/material';
import TabsInput from 'src/components/TabsInput';
import { translateUrgentValue } from 'src/utils/translateUrgentValue';

export interface InfoPropsType {
    onChange: OnChangeType<EditKeysType>;
    editData: ReceivingType;
    editErrors: FormErrorsType;
    newReceiving: boolean;
    disableEdit: boolean;
    permissionEditAll: boolean;
    allowedStatuses: ConstantsCodeType[];
    receivingStatusCodes: ConstantsCodeType[];
    receivingUrgentCodes: ConstantsCodeType[];
    boolNumberCodes: ConstantsCodeType[];
    receivingPriorityCodes: ConstantsCodeType[];
    skladonUser?: number;
}

const InfoIconStyled = styled('span')({
    marginLeft: '10px',

    '& svg': {
        width: '14px',
        height: '14px',
        marginRight: '10px'
    }
});
const Info: FC<InfoPropsType> = (props) => {

    return (
        <Grid>
            <GridItem cols={12}>
                <Typography variant="h2">{translate('receivingDetail.infoSection')}</Typography>
            </GridItem>
            {!props.newReceiving && (
                <GridItem cols={12}>
                    <DateTimePicker
                        value={props.editData.CREATED_AT}
                        name="CREATED_AT"
                        label={translate('receivingDetail.createdAt')}
                        fullWidth
                        disabled
                        type="dateTime"
                    />
                </GridItem>
            )}

            {!props.newReceiving && props.editData.STATUS !== 'awaiting' && (
                <>
                    <GridItem cols={12}>
                        <DateTimePicker
                            value={props.editData.UNLOADED_AT}
                            name="UNLOADED_AT"
                            label={translate('receivingDetail.unloadedAt')}
                            fullWidth
                            disabled
                            type="dateTime"
                        />
                    </GridItem>
                    <GridItem cols={12}>
                        <DateTimePicker
                            value={props.editData.RECEIVED_AT}
                            name="RECEIVED_AT"
                            label={translate('receivingDetail.receivedAt')}
                            fullWidth
                            disabled
                            type="dateTime"
                        />
                    </GridItem>
                    <GridItem cols={12}>
                        <DateTimePicker
                            value={props.editData.CONFIRMED_AT}
                            name="CONFIRMED_AT"
                            label={translate('receivingDetail.confirmedAt')}
                            fullWidth
                            disabled
                            type="dateTime"
                        />
                    </GridItem>
                </>
            )}
            {!props.newReceiving && (
                <GridItem cols={12}>
                    <TextField
                        value={props.editData.ASN}
                        name="FCN"
                        label={translate('receivingDetail.asn')}
                        fullWidth
                        disabled
                    />
                </GridItem>
            )}
            <GridItem cols={12}>
                <TextField
                    value={props.editData.ESHOP_RECEIVING_ID}
                    error={props.editErrors.ESHOP_RECEIVING_ID}
                    onChange={(value, error) => props.onChange('ESHOP_RECEIVING_ID', value, error)}
                    name="ESHOP_RECEIVING_ID"
                    label={translate('receivingDetail.eshopReceivingId')}
                    fullWidth
                    maxLength={50}
                    disabled={props.disableEdit}
                />
            </GridItem>
            {!props.newReceiving && (
                <GridItem cols={12}>
                    <Select
                        options={props.receivingStatusCodes}
                        editableOptions={props.allowedStatuses}
                        value={props.editData.STATUS}
                        error={props.editErrors.STATUS}
                        onChange={(value, error) => props.onChange('STATUS', value, error)}
                        name="STATUS"
                        label={translate('receivingDetail.status')}
                        required
                        fullWidth
                        disabled={props.disableEdit}
                        optionStyleFce={colorOptionFce}
                    />
                </GridItem>
            )}
            <GridItem cols={12}>
                <Select
                    options={props.boolNumberCodes}
                    value={props.editData.DUTY}
                    error={props.editErrors.DUTY}
                    onChange={(value, error) => props.onChange('DUTY', value || 0, error)}
                    name="DUTY"
                    label={<>
                        {translate('receivingDetail.duty')}
                        <Tooltip title={translate('receivingDetail.dutyTooltip')}>
                            <InfoIconStyled>
                                <InfoIcon />
                            </InfoIconStyled>
                        </Tooltip>
                    </>}
                    fullWidth
                    required
                    disabled={props.disableEdit}
                    optionStyleFce={colorOptionFce}
                />
            </GridItem>
            <GridItem cols={12}>
                <TextField
                    value={props.editData.DESCRIPTION}
                    error={props.editErrors.DESCRIPTION}
                    onChange={(value, error) => props.onChange('DESCRIPTION', value || '', error)}
                    name="DESCRIPTION"
                    label={translate('receivingDetail.description')}
                    fullWidth
                    maxLength={255}
                    disabled={props.disableEdit}
                />
            </GridItem>
            <GridItem cols={12} sx={{ marginBottom: '30px' }}>
                <TabsInput
                    options={props.receivingPriorityCodes}
                    value={String(props.editData.PRIORITY)}
                    onChange={(value) => props.onChange('PRIORITY', Number(value), false)}
                    label={translate('receivingDetail.priority')}
                    disabled={props.disableEdit}
                    disabledItems={!props.skladonUser ? [String(props.receivingPriorityCodes[props.receivingPriorityCodes.length - 1].value)] : undefined}
                />
            </GridItem>
            {!props.newReceiving && props.editData.STATUS !== 'awaiting' && (
                <GridItem cols={12}>
                    {(props.receivingUrgentCodes.some((item) => item.value === props.editData.URGENT) && (
                        <Select
                            options={props.receivingUrgentCodes}
                            value={translateUrgentValue(props.editData.URGENT)}
                            error={props.editErrors.URGENT}
                            onChange={(value, error) => props.onChange('URGENT', value, error)}
                            name="URGENT"
                            label={translate('receivingDetail.urgent')}
                            fullWidth
                            disabled
                        />
                    ))}
                    {!props.receivingUrgentCodes.some((item) => item.value === props.editData.URGENT) && (
                        <TextField
                            value={translateUrgentValue(props.editData.URGENT)}
                            name="URGENT"
                            label={translate('receivingDetail.urgent')}
                            fullWidth
                            disabled
                        />
                    )}
                </GridItem>
            )}
            {!props.newReceiving && props.editData.STATUS !== 'awaiting' && (
                <>
                    <GridItem cols={12}>
                        <DateTimePicker
                            value={props.editData.SLA_DATE}
                            name="SLA_DATE"
                            label={translate('receivingDetail.slaDate')}
                            fullWidth
                            disabled
                            type="dateTime"
                        />
                    </GridItem>
                    <GridItem cols={12}>
                        <TextField
                            value={props.editData.SLA_SUSPENDED}
                            name="SLA_SUSPENDED"
                            label={translate('receivingDetail.slaSuspended')}
                            fullWidth
                            disabled
                        />
                    </GridItem>
                    <GridItem cols={12}>
                        <Select
                            options={props.boolNumberCodes}
                            value={normalizeBoolNumberValue(props.editData.PHOTO_DOCUMENTATION_TAKEN)}
                            name="PHOTO_DOCUMENTATION_TAKEN"
                            label={translate('receivingDetail.photoDocumentationTaken')}
                            fullWidth
                            disabled
                            optionStyleFce={colorOptionFce}
                        />
                    </GridItem>
                </>
            )}
            <GridItem cols={12}>
                <TextField
                    value={props.editData.ESHOP_NOTE}
                    error={props.editErrors.ESHOP_NOTE}
                    onChange={(value, error) => props.onChange('ESHOP_NOTE', value, error)}
                    name="ESHOP_NOTE"
                    label={translate('receivingDetail.eshopNote')}
                    fullWidth
                    multiline
                />
            </GridItem>
        </Grid>
    )
        ;
};

export default Info;
